// TODO: Review AI Code for feature: Process Map - Entire file is AI-generated
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, X } from 'lucide-react';
import {
  Dialog,
  DialogShell,
  DialogShellBody,
  DialogShellFooter,
  DialogShellHeader,
  Combobox,
  ComboboxTrigger,
  ComboboxContent,
  ComboboxInput,
  ComboboxEmpty,
  ComboboxList,
  ComboboxItem,
  Badge,
  Button,
  Skeleton
} from '@bliks-io/ui';
import type { ProcessMapNode } from '@bliks-io/contracts';
import { filterProcessesBySearch } from '@/lib/process-utils';
import { LoadErrorRetry } from '@/components/load-error-retry/LoadErrorRetry';
import { useProcessGraphHelpers } from './useProcessGraphHelpers';

// TODO: Review AI Code for feature: Process Map - Entire file is AI-generated

interface MappingEntry {
  processId: string;
  processName: string;
  departmentName?: string;
}

const CATEGORY_BADGE_COLORS: Record<string, string> = {
  management: 'bg-orange-100 text-orange-700',
  core: 'bg-emerald-100 text-emerald-700',
  support: 'bg-slate-100 text-slate-700'
};

interface AutoDetectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  chainName: string;
  availableProcesses: { _id: string; name: string; department?: { name: string } | null }[];
  currentMappingIds: Set<string>;
  onConfirm: (entries: MappingEntry[]) => void;
  processesLoading: boolean;
}

export function AutoDetectDialog({
  open,
  onOpenChange,
  chainName,
  availableProcesses,
  currentMappingIds,
  onConfirm,
  processesLoading
}: AutoDetectDialogProps) {
  const { t } = useTranslation();
  const { autoDetectChain, processLookup, graphState } = useProcessGraphHelpers();
  const [seedId, setSeedId] = useState<string | null>(null);
  const [removedIds, setRemovedIds] = useState<Set<string>>(new Set());
  const [seedSearch, setSeedSearch] = useState('');

  const filteredAvailable = useMemo(
    () => filterProcessesBySearch(availableProcesses, seedSearch),
    [availableProcesses, seedSearch]
  );

  // Detected chain (topologically sorted)
  const detectedIds = useMemo(() => {
    if (!seedId) return [];
    return autoDetectChain(seedId, currentMappingIds);
  }, [seedId, autoDetectChain, currentMappingIds]);

  // Filter out manually removed items
  const filteredIds = useMemo(
    () => detectedIds.filter((id) => !removedIds.has(id)),
    [detectedIds, removedIds]
  );

  // Resolved process info for display
  const detectedProcesses = useMemo(
    () =>
      filteredIds.map((id) => processLookup.get(id)).filter((p): p is ProcessMapNode => p != null),
    [filteredIds, processLookup]
  );

  const handleSeedChange = (value: string | string[] | undefined) => {
    if (!value || Array.isArray(value)) return;
    setSeedId(value);
    setRemovedIds(new Set());
  };

  const handleRemove = (id: string) => {
    setRemovedIds((prev) => new Set([...prev, id]));
  };

  const handleConfirm = () => {
    const entries: MappingEntry[] = detectedProcesses.map((p) => ({
      processId: p._id,
      processName: p.name,
      departmentName: p.department.name
    }));
    onConfirm(entries);
    // Reset state
    setSeedId(null);
    setRemovedIds(new Set());
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setSeedId(null);
      setRemovedIds(new Set());
      setSeedSearch('');
    }
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogShell size="md">
        <DialogShellHeader
          icon={<Sparkles className="h-5 w-5" />}
          title={`${t('processChains.edit.autoDetect.title')} — ${chainName}`}
          subtitle={t('processChains.edit.autoDetect.description')}
          onClose={() => handleOpenChange(false)}
        />
        <DialogShellBody>
          <div className="flex flex-col gap-4">
            {/* Seed process picker */}
            <Combobox value={seedId ?? undefined} onValueChange={handleSeedChange}>
              <ComboboxTrigger className="w-full">
                {seedId
                  ? (availableProcesses.find((p) => p._id === seedId)?.name ?? seedId)
                  : t('processChains.edit.autoDetect.selectSeed')}
              </ComboboxTrigger>
              <ComboboxContent>
                <ComboboxInput
                  placeholder={t('processChains.edit.autoDetect.selectSeed')}
                  value={seedSearch}
                  onValueChange={setSeedSearch}
                />
                <ComboboxList>
                  <ComboboxEmpty>
                    {processesLoading ? t('common.loading') : t('processes.empty')}
                  </ComboboxEmpty>
                  {filteredAvailable.map((proc) => (
                    <ComboboxItem key={proc._id} value={proc._id}>
                      <div className="flex flex-col">
                        <span>{proc.name}</span>
                        {proc.department?.name && (
                          <span className="text-xs text-muted-foreground">
                            {proc.department.name}
                          </span>
                        )}
                      </div>
                    </ComboboxItem>
                  ))}
                </ComboboxList>
              </ComboboxContent>
            </Combobox>

            {/* Detected chain preview */}
            {graphState?.status === 'failed' && (
              <LoadErrorRetry message={t('common.errorOccurred')} onRetry={graphState.retry} />
            )}
            {graphState?.status === 'pending' && seedId && (
              <div role="status" aria-label={t('common.loading')} className="flex flex-col gap-1.5">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            )}
            {!graphState && seedId && detectedProcesses.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">
                {t('processChains.edit.autoDetect.noConnections')}
              </p>
            )}

            {detectedProcesses.length > 0 && (
              <div className="flex flex-col gap-1.5 max-h-[300px] overflow-y-auto">
                {detectedProcesses.map((proc, index) => (
                  <div key={proc._id} className="flex items-center gap-2 rounded-md border p-2">
                    <span className="text-xs font-mono text-muted-foreground w-6 text-center shrink-0">
                      {index + 1}
                    </span>
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="text-sm font-medium truncate">{proc.name}</span>
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-muted-foreground truncate">
                          {proc.department.name}
                        </span>
                        {proc.category && (
                          <Badge
                            variant="outline"
                            size="sm"
                            className={CATEGORY_BADGE_COLORS[proc.category] ?? ''}
                          >
                            {t(`processChains.categories.${proc.category}`)}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 shrink-0 text-muted-foreground hover:text-destructive"
                      onClick={() => handleRemove(proc._id)}
                      title={t('processChains.edit.remove')}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogShellBody>
        <DialogShellFooter
          hint={
            detectedProcesses.length > 0
              ? t('processChains.edit.autoDetect.detected', {
                  count: detectedProcesses.length
                })
              : undefined
          }
          secondary={{
            label: t('common.cancel'),
            onClick: () => handleOpenChange(false)
          }}
          primary={{
            label: t('processChains.edit.autoDetect.addAll'),
            onClick: handleConfirm,
            disabled: detectedProcesses.length === 0
          }}
        />
      </DialogShell>
    </Dialog>
  );
}
